import { Box, Divider, Stack, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { URL_REGEX } from "../../../../utils/regex";
import {
  CampaignSavedData,
  ProductAnalysisResponse,
} from "../../../../types/apiTypes";
import { useAppDispatch } from "../../../../hooks";
import { setStatus } from "../../../../features/companySlice";
import { SSEEvents } from "../../../../types/companyTypes";
import { CustomButton } from "components/ui/CustomButton";
import { FileDropZone, ReuseInput, FileList } from "../../../ui";
import { AnalysePDFBlock } from "../../../ui/modal";
import { toast } from "react-toastify";
import { CustomToast } from "../../../ui/CustomToast";
import { clearCreateFlowLocalStorage } from "../../../../utils/clearCreateFlowLocalStorage";
import { useGetCampaignFieldsQuery } from "../../../../api/referencesApi";
import { trackEvent } from "../../../../utils/analyticsService";
import {
  AnalyticsEvent,
  AnalyticsStatus,
  InputType,
} from "../../../../types/events";

type Fields = {
  product_url: string;
};

type Props = {
  onCancel?: () => void;
  initProductUrl: string;
  setResultData: (
    data: CampaignSavedData,
    file: File[] | null,
    url: string,
  ) => void;
  nextStepHandler: () => void;
  initPDFFiles: File[];
  returnBackHandler: () => void;
};

type Errors = { [key: string]: boolean };

const validation: { [key: string]: (value: string) => boolean } = {
  product_url: (value: string) => !!value && !URL_REGEX.test(value),
};

export const UploadProductInfoStep = ({
  initProductUrl,
  setResultData,
  nextStepHandler,
  initPDFFiles,
  returnBackHandler,
}: Props) => {
  const dispatch = useAppDispatch();

  const { data } = useGetCampaignFieldsQuery();

  const [analyze, setAnalyze] = useState<boolean>(false);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>(initPDFFiles);
  const analyzeStartedAt = useRef<number | null>(null);

  const [formData, setFormData] = useState<Fields>({
    product_url: initProductUrl,
  });

  const [error, setError] = useState<Errors>({
    product_url: false,
    product_abstract: false,
    product_name: false,
    url: false,
  });

  useEffect(() => {
    dispatch(setStatus({ [SSEEvents.generation_status]: undefined }));
  }, []);

  const inputChangeHandler = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError((prev) => ({
      ...prev,
      [field]: validation[field](value),
    }));
  };

  const setErrorHandler = (field: string, value: boolean) => {
    setError((prev) => ({ ...prev, [field]: value }));
  };

  const submitHandler = () => {
    const isSameFiles =
      initPDFFiles.length === uploadedFiles.length &&
      initPDFFiles.every((file, idx) => file === uploadedFiles[idx]);

    if (isSameFiles && initProductUrl === formData.product_url) {
      nextStepHandler();
      return;
    }
    analyzeStartedAt.current = Date.now();
    setAnalyze(true);
  };

  const onFileUpload = (files: File[]) => {
    setUploadedFiles((prev) => [...prev, ...files]);
  };

  const setResultHandler = (result: ProductAnalysisResponse) => {
    setAnalyze(false);

    const duration_ms = analyzeStartedAt.current
      ? Date.now() - analyzeStartedAt.current
      : 0;
    const input_type = uploadedFiles.length > 0 ? "file_upload" : "url";

    const target_regions =
      result.target_regions && result.target_regions.length > 0
        ? result.target_regions.map((region) => ({
            name: region,
            countries: [],
            is_all_countries: true,
          }))
        : [];

    const target_industries = (result.target_industries || []).map(
      (industry) => ({
        name: industry,
        sub_industries: [],
        is_all_sub_industries: true,
      }),
    );

    const target_account_sizes = data?.account_sizes.filter(({ min_max }) =>
      (result.target_account_size || []).includes(min_max),
    );
    const target_revenue = (result.target_revenue || []).map((value) => ({
      id: value,
      value,
      isAdded: false,
    }));

    const initialData = {
      target_industries,
      target_audience: [],
      target_regions,
      target_account_sizes,
      target_revenue,
    };

    if (result.error) {
      const { files, url } = result.error;

      const validFiles = files
        ? uploadedFiles.filter((file) => !files?.includes(file.name))
        : uploadedFiles;

      const validUrl = url ? "" : formData.product_url;

      setUploadedFiles(validFiles);
      setFormData({ product_url: validUrl });
      setResultData(
        { ...result, ...initialData, product_url: validUrl },
        validFiles,
        validUrl,
      );

      trackEvent(AnalyticsEvent.OfferingInfoCompleted, {
        input_type:
          uploadedFiles.length > 0 ? InputType.FileUpload : InputType.Url,
        status: AnalyticsStatus.Failure,
        duration_ms,
      });

      toast.error(
        <CustomToast
          type={"error"}
          text={
            "The content of the attached PDF file(s) doesn't match the provided URL or other attached PDFs. \n" +
            "Please review the file(s) before proceeding."
          }
          title={"Attachment Failed"}
        />,
      );
    } else {
      trackEvent(AnalyticsEvent.OfferingInfoCompleted, {
        input_type:
          uploadedFiles.length > 0 ? InputType.FileUpload : InputType.Url,
        status: AnalyticsStatus.Success,
        duration_ms,
      });

      setResultData(
        { ...result, ...initialData, product_url: formData.product_url },
        uploadedFiles,
        formData.product_url,
      );
      nextStepHandler();
    }
  };

  const changeFileHandler = (fileName: string, file: File) => {
    if (analyze) return;
    setUploadedFiles((state) =>
      state.map((el) => {
        if (el.name !== fileName) return el;
        return file;
      }),
    );
  };

  const removeFileHandler = (fileName: string) => {
    if (analyze) return;
    setUploadedFiles((state) => state.filter(({ name }) => name !== fileName));
  };

  return (
    <Stack gap="16px" width="100%">
      <Stack gap="16px">
        <Stack>
          <ReuseInput
            name="product_url"
            handleInputChange={inputChangeHandler}
            errorMessage={
              <Stack component="span" alignItems="left" width="100%">
                <span>Invalid URL: Please check the format and try again.</span>
              </Stack>
            }
            value={formData.product_url}
            label="Insert Product, Solution or Service Description Page URL"
            placeholder="Example: https://www.example.com"
            isError={error.product_url}
            setErrorHandler={setErrorHandler}
            isDisabled={analyze}
          />
        </Stack>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Divider sx={{ flexGrow: 1, color: "#CFCFCF" }} />
          <Typography
            variant="button"
            sx={{
              marginX: "8px",
              color: "#4C545B",
            }}
          >
            and / or
          </Typography>
          <Divider sx={{ flexGrow: 1, color: "#CFCFCF" }} />
        </Box>

        <FileList
          isDisabled={analyze}
          files={uploadedFiles}
          removeFileHandler={removeFileHandler}
          changeFileHandler={changeFileHandler}
          mimeTypes={["application/pdf"]}
          acceptedExtensions={["pdf"]}
        />

        {analyze ? (
          <AnalysePDFBlock
            files={uploadedFiles}
            setResultHandler={setResultHandler}
            productUrl={formData.product_url}
            stopAnalyzeHandler={() => setAnalyze(false)}
          />
        ) : (
          <FileDropZone
            uploadedFiles={uploadedFiles}
            onFileUpload={onFileUpload}
            acceptedExtensions={["pdf"]}
            mimeTypes={["application/pdf"]}
            label="Drag PDF files with your offering description here"
          />
        )}
      </Stack>
      <Stack direction="row" gap="8px">
        <CustomButton
          variant="contained"
          textColor="#4C545B"
          fullWidth
          type="button"
          customColor="#F1F1F1"
          disabled={analyze}
          onClick={() => {
            clearCreateFlowLocalStorage();
            returnBackHandler();
          }}
        >
          Cancel
        </CustomButton>
        <CustomButton
          onClick={submitHandler}
          variant="contained"
          fullWidth
          type="button"
          customColor="#1C3C6C"
          disabled={
            analyze ||
            (uploadedFiles.length === 0 && !formData.product_url) ||
            error.product_url
          }
        >
          Next Step
        </CustomButton>
      </Stack>
    </Stack>
  );
};
